// Context/LearningContext.tsx

import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  Dispatch,
  SetStateAction,
} from "react";
import axios from "axios";
import { getAccessToken } from "../Components/Utils/TokenRefresh";
import { Question, Section } from "../Models/Types";

interface QueuedQuestion {
  question?: Question;
  sectionIndex: number;
  unitIndex: number;
  questionIndex: number;
}

/* ⭐ View mode type */
type LearningViewMode = "cards" | "journey";

interface LearningContextType {
  currentSubject: string;
  userType: string;
  currentSectionIndex: number;
  currentUnitIndex: number;
  currentQuestionIndex: number;
  sections: Section[];

  // ⭐ NEW
  activeLearningSection: Section | null;
  setActiveLearningSection: Dispatch<SetStateAction<Section | null>>;

  setCurrentSubject: Dispatch<SetStateAction<string>>;
  setUserType: Dispatch<SetStateAction<string>>;
  setCurrentSectionIndex: Dispatch<SetStateAction<number>>;
  setCurrentUnitIndex: Dispatch<SetStateAction<number>>;
  setCurrentQuestionIndex: Dispatch<SetStateAction<number>>;
  setSections: Dispatch<SetStateAction<Section[]>>;
  setCloseQuiz: Dispatch<SetStateAction<() => void>>;

  loadNextQuestion: () => void;
  fetchSections: () => Promise<void>;
  setNextQuestion: () => void;
  closeQuiz: () => void;

  isRetryMode: boolean;
  setIsRetryMode: Dispatch<SetStateAction<boolean>>;
  incorrectQuestionsQueue: QueuedQuestion[];
  setIncorrectQuestionsQueue: Dispatch<SetStateAction<QueuedQuestion[]>>;

  quizOpen: boolean; // ✅ ADD THIS
  setQuizOpen: Dispatch<SetStateAction<boolean>>;

  loading: boolean;

  isStreakOpen: boolean;
  setIsStreakOpen: Dispatch<SetStateAction<boolean>>;

  isEndOfUnitOpen: boolean;
  setIsEndOfUnitOpen: Dispatch<SetStateAction<boolean>>;

  isUnitComplete: boolean;
  setIsUnitComplete: Dispatch<SetStateAction<boolean>>;

  justCompletedUnit: {
    sectionId: string;
    unitId: string;
    completed: boolean;
  } | null;
  setJustCompletedUnit: Dispatch<
    SetStateAction<{
      sectionId: string;
      unitId: string;
      completed: boolean;
    } | null>
  >;

  starsEarned: number;
  setStarsEarned: Dispatch<SetStateAction<number>>;

  feedbackVisible: boolean;
  setFeedbackVisible: Dispatch<SetStateAction<boolean>>;

  hintUsedForCurrentQuestion: boolean;
  setHintUsedForCurrentQuestion: Dispatch<SetStateAction<boolean>>;

  correctQuestionCount: number;
  setCorrectQuestionCount: Dispatch<SetStateAction<number>>;

  wrongQuestionCount: number;
  setWrongQuestionCount: Dispatch<SetStateAction<number>>;

  correctRetryQuestionCount: number;
  setCorrectRetryQuestionCount: Dispatch<SetStateAction<number>>;

  resetQuestionCounters: () => void;

  /* ⭐ View mode */
  viewMode: LearningViewMode;
  setViewMode: Dispatch<SetStateAction<LearningViewMode>>;
}

const LearningContext = createContext<LearningContextType | undefined>(
  undefined,
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CURRENT_INDICES_KEY = "currentQuestionIndices";

export const LearningProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Empty means "the active course": the server resolves the subject from it,
  // so a French, German, Japanese or Mandarin learner gets their own lessons.
  // MathRoadmap sets "math" explicitly.
  const [currentSubject, setCurrentSubject] = useState<string>("");
  // Only the newest request may set sections (a slow earlier one must not win).
  const fetchSeq = React.useRef(0);
  const [userType, setUserType] = useState<string>("kid");
  const [currentSectionIndex, setCurrentSectionIndex] = useState<number>(0);
  const [currentUnitIndex, setCurrentUnitIndex] = useState<number>(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [sections, setSections] = useState<Section[]>([]);

  // ⭐ NEW
  const [activeLearningSection, setActiveLearningSection] =
    useState<Section | null>(null);

  /* ⭐ View mode state (FIX) */
  const [viewMode, setViewMode] = useState<LearningViewMode>("cards");

  const [isRetryMode, setIsRetryMode] = useState(false);
  const [incorrectQuestionsQueue, setIncorrectQuestionsQueue] = useState<
    QueuedQuestion[]
  >([]);
  const [loading, setLoading] = useState(true);

  const [quizOpen, setQuizOpen] = useState(false); // ✅ already here
  const [closeQuiz, setCloseQuiz] = useState<() => void>(() => () => {});

  const [isStreakOpen, setIsStreakOpen] = useState(false);
  const [isEndOfUnitOpen, setIsEndOfUnitOpen] = useState(false);
  const [isUnitComplete, setIsUnitComplete] = useState(false);
  const [justCompletedUnit, setJustCompletedUnit] = useState<{
    sectionId: string;
    unitId: string;
    completed: boolean;
  } | null>(null);

  const [starsEarned, setStarsEarned] = useState<number>(() => {
    const saved = localStorage.getItem("starsEarned");
    return saved ? parseInt(saved, 10) : 0;
  });

  const [feedbackVisible, setFeedbackVisible] = useState<boolean>(false);
  const [hintUsedForCurrentQuestion, setHintUsedForCurrentQuestion] = useState<boolean>(false);

  const [correctQuestionCount, setCorrectQuestionCount] = useState<number>(0);
  const [wrongQuestionCount, setWrongQuestionCount] = useState<number>(0);
  const [correctRetryQuestionCount, setCorrectRetryQuestionCount] =
    useState<number>(0);

  const resetQuestionCounters = () => {
    setCorrectQuestionCount(0);
    setWrongQuestionCount(0);
    setCorrectRetryQuestionCount(0);
  };

  useEffect(() => {
    localStorage.setItem("starsEarned", starsEarned.toString());
  }, [starsEarned]);

  const fetchSections = async () => {
    const seq = ++fetchSeq.current;
    try {
      const token = await getAccessToken();
      if (!token) {
        setLoading(false);
        return;
      }
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/sections/fetchSections`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: currentSubject ? { subject: currentSubject } : undefined,
        },
      );
      if (seq !== fetchSeq.current) return;
      const payload = response.data;
      const list = Array.isArray(payload) ? payload : payload?.sections;
      setSections(list || []);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching sections: ", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSections();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSubject]);

  return (
    <LearningContext.Provider
      value={{
        currentSubject,
        userType,
        currentSectionIndex,
        currentUnitIndex,
        currentQuestionIndex,
        sections,

        activeLearningSection,
        setActiveLearningSection,

        setCurrentSubject,
        setUserType,
        setCurrentSectionIndex,
        setCurrentUnitIndex,
        setCurrentQuestionIndex,
        setSections,

        loadNextQuestion: () => {},
        fetchSections,
        setNextQuestion: () => {},

        isRetryMode,
        setIsRetryMode,
        incorrectQuestionsQueue,
        setIncorrectQuestionsQueue,

        loading,

        quizOpen, // ✅ ADD THIS
        setQuizOpen,

        closeQuiz,
        setCloseQuiz,

        isStreakOpen,
        setIsStreakOpen,
        isEndOfUnitOpen,
        setIsEndOfUnitOpen,
        isUnitComplete,
        setIsUnitComplete,
        justCompletedUnit,
        setJustCompletedUnit,

        starsEarned,
        setStarsEarned,

        feedbackVisible,
        setFeedbackVisible,

        hintUsedForCurrentQuestion,
        setHintUsedForCurrentQuestion,

        correctQuestionCount,
        setCorrectQuestionCount,
        wrongQuestionCount,
        setWrongQuestionCount,

        correctRetryQuestionCount,
        setCorrectRetryQuestionCount,

        resetQuestionCounters,

        /* ⭐ FIXED */
        viewMode,
        setViewMode,
      }}
    >
      {children}
    </LearningContext.Provider>
  );
};

export const useLearningContext = () => {
  const context = useContext(LearningContext);
  if (!context) {
    throw new Error(
      "useLearningContext must be used within a LearningProvider",
    );
  }
  return context;
};
